import React, { useState, useRef, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  SafeAreaView,
  Alert,
  ActivityIndicator,
  Switch,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import * as FileSystem from 'expo-file-system';
import * as Sharing from 'expo-sharing';
import { AppLayout } from '../../components/layout/AppLayout';
import { useDriverStore } from '../../stores/driverStore';
import theme from '../../theme';

interface ExportProgress {
  phase: 'idle' | 'preparing' | 'fetching' | 'processing' | 'generating' | 'complete' | 'error';
  currentStep: number;
  totalSteps: number;
  message: string;
  details?: string;
  processed?: number;
  total?: number;
}

interface ExportStats {
  totalRecords: number;
  exportedRecords: number;
  fileSize: string;
  format: string;
  fieldsIncluded: number;
  totalFields: number;
  platformBreakdown: {
    uber: number;
    careem: number;
    both: number;
    none: number;
  };
}

interface ExportField {
  key: string;
  label: string;
  description: string;
  enabled: boolean;
  required?: boolean;
}

export const DriverExportScreen = () => {
  const navigation = useNavigation();
  const { drivers, fetchDrivers } = useDriverStore();
  const [selectedFormat, setSelectedFormat] = useState<'csv' | 'json' | 'excel'>('csv');
  const [progress, setProgress] = useState<ExportProgress>({
    phase: 'idle',
    currentStep: 0,
    totalSteps: 5,
    message: 'Ready to export Driver database',
  });
  const [exportStats, setExportStats] = useState<ExportStats | null>(null);

  const [exportFields, setExportFields] = useState<ExportField[]>([
    { key: 'id', label: 'Driver ID', description: 'Unique driver identifier', enabled: true, required: true },
    { key: 'name', label: 'Full Name', description: 'Driver full name', enabled: true, required: true },
    { key: 'phone', label: 'Phone Number', description: 'Contact phone number', enabled: true },
    { key: 'email', label: 'Email Address', description: 'Email contact', enabled: true },
    { key: 'license_number', label: 'License Number', description: 'Driving license number', enabled: true },
    { key: 'uber_uuid', label: 'Uber UUID', description: 'Uber platform identifier', enabled: true },
    { key: 'careem_captain_id', label: 'Careem Captain ID', description: 'Careem platform identifier', enabled: true },
    { key: 'national_id', label: 'National ID', description: 'Government ID number', enabled: false },
    { key: 'address', label: 'Address', description: 'Home address', enabled: false },
    { key: 'hire_date', label: 'Hire Date', description: 'Date when driver was hired', enabled: true },
    { key: 'status', label: 'Status', description: 'Active/Inactive status', enabled: true },
    { key: 'vehicle_assigned', label: 'Vehicle Assigned', description: 'Currently assigned vehicle', enabled: false },
    { key: 'created_at', label: 'Created Date', description: 'Record creation date', enabled: false },
    { key: 'updated_at', label: 'Last Updated', description: 'Last modification date', enabled: false },
  ]);

  const exportFormats = [
    { id: 'csv', name: 'CSV', description: 'Comma-separated values', icon: 'document-text', color: '#059669' },
    { id: 'json', name: 'JSON', description: 'JavaScript Object Notation', icon: 'code-slash', color: '#DC2626' },
    { id: 'excel', name: 'Excel', description: 'Microsoft Excel format', icon: 'grid', color: '#16A34A' },
  ];

  const steps = [
    { id: 1, name: 'Format Selection', description: 'Choose export format and configure fields' },
    { id: 2, name: 'Data Fetching', description: 'Retrieve driver records from database' },
    { id: 3, name: 'Data Processing', description: 'Format and filter selected data fields' },
    { id: 4, name: 'File Generation', description: 'Generate export file in selected format' },
    { id: 5, name: 'Download Ready', description: 'File ready for download or sharing' },
  ];

  useEffect(() => {
    fetchDrivers();
  }, []);

  const toggleField = (fieldKey: string) => {
    setExportFields(prev => 
      prev.map(field => 
        field.key === fieldKey 
          ? { ...field, enabled: field.required ? true : !field.enabled }
          : field
      )
    );
  };

  const startExport = async () => {
    const enabledFields = exportFields.filter(f => f.enabled);
    if (enabledFields.length === 0) {
      Alert.alert('Error', 'Please select at least one field to export');
      return;
    }

    setProgress({ phase: 'preparing', currentStep: 1, totalSteps: 5, message: 'Preparing export...' });
    await new Promise(resolve => setTimeout(resolve, 800));

    setProgress({ 
      phase: 'fetching', 
      currentStep: 2, 
      totalSteps: 5, 
      message: 'Fetching driver records...',
      details: 'Connecting to database and retrieving driver data'
    });
    await new Promise(resolve => setTimeout(resolve, 1200));

    setProgress({ 
      phase: 'processing', 
      currentStep: 3, 
      totalSteps: 5, 
      message: 'Processing driver data...',
      details: 'Filtering and formatting selected fields'
    });
    
    // Process real driver data
    const totalDrivers = drivers.length;
    const platformBreakdown = {
      uber: drivers.filter(d => d.uber_driver_uuid && !d.careem_captain_id).length,
      careem: drivers.filter(d => d.careem_captain_id && !d.uber_driver_uuid).length,
      both: drivers.filter(d => d.uber_driver_uuid && d.careem_captain_id).length,
      none: drivers.filter(d => !d.uber_driver_uuid && !d.careem_captain_id).length,
    };
    
    for (let i = 0; i <= 100; i += 20) {
      setProgress({ 
        phase: 'processing', 
        currentStep: 3, 
        totalSteps: 5, 
        message: 'Processing driver data...',
        details: `Processed ${i}% of records`,
        processed: Math.floor((i / 100) * totalDrivers),
        total: totalDrivers
      });
      await new Promise(resolve => setTimeout(resolve, 150));
    }

    setProgress({ 
      phase: 'generating', 
      currentStep: 4, 
      totalSteps: 5, 
      message: `Generating ${selectedFormat.toUpperCase()} file...`,
      details: 'Creating downloadable file with selected data'
    });
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Set final results with real data (totalDrivers already declared above)
    const estimatedSize = Math.round((totalDrivers * enabledFields.length * 12) / 1024); // Rough estimate
    const stats: ExportStats = {
      totalRecords: totalDrivers,
      exportedRecords: totalDrivers,
      fileSize: selectedFormat === 'json' ? `${Math.round(estimatedSize * 1.8)} KB` : 
                selectedFormat === 'excel' ? `${Math.round(estimatedSize * 1.4)} KB` : 
                `${estimatedSize} KB`,
      format: selectedFormat.toUpperCase(),
      fieldsIncluded: enabledFields.length,
      totalFields: exportFields.length,
      platformBreakdown,
    };

    setExportStats(stats);
    setProgress({ 
      phase: 'complete', 
      currentStep: 5, 
      totalSteps: 5, 
      message: 'Export completed successfully!',
      details: `${stats.exportedRecords} driver records exported to ${stats.format} format`
    });
  };

  const downloadFile = async () => {
    try {
      if (!exportStats) return;
      
      const enabledFields = exportFields.filter(f => f.enabled);
      let fileContent = '';
      let fileName = `driver_export_${new Date().toISOString().split('T')[0]}`;
      
      if (selectedFormat === 'csv') {
        const headers = enabledFields.map(f => f.label).join(',');
        const rows = drivers.map(driver => 
          enabledFields.map(field => {
            const value = driver[field.key as keyof typeof driver] || '';
            return `"${String(value).replace(/"/g, '""')}"`;
          }).join(',')
        );
        fileContent = [headers, ...rows].join('\n');
        fileName += '.csv';
      } else if (selectedFormat === 'json') {
        const data = drivers.map(driver => {
          const filtered: any = {};
          enabledFields.forEach(field => {
            filtered[field.key] = driver[field.key as keyof typeof driver];
          });
          return filtered;
        });
        fileContent = JSON.stringify(data, null, 2);
        fileName += '.json';
      }
      
      const fileUri = FileSystem.documentDirectory + fileName;
      await FileSystem.writeAsStringAsync(fileUri, fileContent);
      await Sharing.shareAsync(fileUri);
      
      Alert.alert('Success', `${exportStats.format} file downloaded successfully!`);
    } catch (error) {
      console.error('Download error:', error);
      Alert.alert('Error', 'Failed to download file');
    }
  };

  const shareFile = async () => {
    try {
      if (!exportStats) return;
      
      const enabledFields = exportFields.filter(f => f.enabled);
      let fileContent = '';
      let fileName = `driver_export_${new Date().toISOString().split('T')[0]}`;
      
      if (selectedFormat === 'csv') {
        const headers = enabledFields.map(f => f.label).join(',');
        const rows = drivers.map(driver => 
          enabledFields.map(field => {
            const value = driver[field.key as keyof typeof driver] || '';
            return `"${String(value).replace(/"/g, '""')}"`;
          }).join(',')
        );
        fileContent = [headers, ...rows].join('\n');
        fileName += '.csv';
      } else if (selectedFormat === 'json') {
        const data = drivers.map(driver => {
          const filtered: any = {};
          enabledFields.forEach(field => {
            filtered[field.key] = driver[field.key as keyof typeof driver];
          });
          return filtered;
        });
        fileContent = JSON.stringify(data, null, 2);
        fileName += '.json';
      }
      
      const fileUri = FileSystem.documentDirectory + fileName;
      await FileSystem.writeAsStringAsync(fileUri, fileContent);
      await Sharing.shareAsync(fileUri);
    } catch (error) {
      console.error('Share error:', error);
      Alert.alert('Error', 'Failed to share file');
    }
  };

  const resetExport = () => {
    setProgress({
      phase: 'idle',
      currentStep: 0,
      totalSteps: 5,
      message: 'Ready to export Driver database',
    });
    setExportStats(null);
  };

  const getStepStatus = (stepNumber: number) => {
    if (progress.currentStep > stepNumber) return 'complete';
    if (progress.currentStep === stepNumber) return 'active';
    return 'pending';
  };

  const getStepColor = (status: string) => {
    switch (status) {
      case 'complete': return theme.colors.success;
      case 'active': return '#DC2626'; // Driver export red
      default: return theme.colors.gray400;
    }
  };

  const StepIndicator = ({ step, status }: { step: any; status: string }) => (
    <View style={styles.stepItem}>
      <View style={[styles.stepCircle, { borderColor: getStepColor(status) }]}>
        {status === 'complete' ? (
          <Ionicons name="checkmark" size={16} color={theme.colors.success} />
        ) : status === 'active' ? (
          <ActivityIndicator size="small" color="#DC2626" />
        ) : (
          <Text style={[styles.stepNumber, { color: getStepColor(status) }]}>{step.id}</Text>
        )}
      </View>
      <View style={styles.stepInfo}>
        <Text style={[styles.stepName, { color: getStepColor(status) }]}>{step.name}</Text>
        <Text style={styles.stepDescription}>{step.description}</Text>
      </View>
    </View>
  );

  return (
    <AppLayout title="Driver Export" showBackButton onBackPress={() => navigation.goBack()}>
      <SafeAreaView style={styles.container}>
        <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
          {/* Header */}
          <View style={styles.header}>
            <View style={[styles.iconContainer, { backgroundColor: '#DC2626' + '15' }]}>
              <Ionicons name="download" size={24} color="#DC2626" />
            </View>
            <View style={styles.headerInfo}>
              <Text style={styles.title}>Driver Database Export</Text>
              <Text style={styles.subtitle}>Export driver profiles with customizable fields and formats</Text>
            </View>
          </View>

          {/* Format Selection */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>1. Export Format</Text>
            <View style={styles.formatGrid}>
              {exportFormats.map((format) => (
                <TouchableOpacity
                  key={format.id}
                  style={[
                    styles.formatCard,
                    selectedFormat === format.id && { 
                      borderColor: format.color,
                      backgroundColor: format.color + '10'
                    }
                  ]}
                  onPress={() => setSelectedFormat(format.id as any)}
                >
                  <View style={[styles.formatIcon, { backgroundColor: format.color + '15' }]}>
                    <Ionicons name={format.icon as any} size={20} color={format.color} />
                  </View>
                  <Text style={[styles.formatName, selectedFormat === format.id && { color: format.color }]}>
                    {format.name}
                  </Text>
                  <Text style={styles.formatDescription}>{format.description}</Text>
                  {selectedFormat === format.id && (
                    <View style={[styles.selectedIndicator, { backgroundColor: format.color }]}>
                      <Ionicons name="checkmark" size={12} color={theme.colors.white} />
                    </View>
                  )}
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {/* Field Selection */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>2. Field Selection</Text>
            <Text style={styles.sectionSubtitle}>
              Choose which driver information to include in the export
            </Text>
            <View style={styles.fieldsContainer}>
              {exportFields.map((field) => (
                <TouchableOpacity
                  key={field.key}
                  style={[
                    styles.fieldItem,
                    field.enabled && styles.fieldItemEnabled,
                    field.required && styles.fieldItemRequired
                  ]}
                  onPress={() => toggleField(field.key)}
                  disabled={field.required}
                >
                  <View style={styles.fieldInfo}>
                    <Text style={[
                      styles.fieldLabel,
                      field.enabled && styles.fieldLabelEnabled,
                      field.required && styles.fieldLabelRequired
                    ]}>
                      {field.label}
                      {field.required && <Text style={styles.requiredMark}> *</Text>}
                    </Text>
                    <Text style={styles.fieldDescription}>{field.description}</Text>
                  </View>
                  <Switch
                    value={field.enabled}
                    onValueChange={() => toggleField(field.key)}
                    disabled={field.required}
                    trackColor={{ false: theme.colors.gray300, true: '#DC2626' + '30' }}
                    thumbColor={field.enabled ? '#DC2626' : theme.colors.gray400}
                  />
                </TouchableOpacity>
              ))}
            </View>
            <View style={styles.fieldSummary}>
              <Text style={styles.fieldSummaryText}>
                {exportFields.filter(f => f.enabled).length} of {exportFields.length} fields selected
              </Text>
            </View>
          </View>

          {/* Progress Steps */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>3. Export Progress</Text>
            <View style={styles.stepsContainer}>
              {steps.map((step) => (
                <StepIndicator 
                  key={step.id} 
                  step={step} 
                  status={getStepStatus(step.id)} 
                />
              ))}
            </View>

            {progress.phase !== 'idle' && (
              <View style={[styles.progressCard, { borderLeftColor: '#DC2626' }]}>
                <View style={styles.progressHeader}>
                  <Text style={styles.progressMessage}>{progress.message}</Text>
                  {(progress.phase === 'preparing' || progress.phase === 'fetching' || 
                    progress.phase === 'processing' || progress.phase === 'generating') && (
                    <ActivityIndicator size="small" color="#DC2626" />
                  )}
                </View>
                {progress.details && (
                  <Text style={styles.progressDetails}>{progress.details}</Text>
                )}
                {progress.processed && progress.total && (
                  <View style={styles.progressBar}>
                    <View 
                      style={[
                        styles.progressFill, 
                        { 
                          width: `${(progress.processed / progress.total) * 100}%`,
                          backgroundColor: '#DC2626'
                        }
                      ]} 
                    />
                  </View>
                )}
              </View>
            )}
          </View>

          {/* Export Statistics */}
          {exportStats && (
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>4. Export Results</Text>
              <View style={styles.statsContainer}>
                <View style={styles.statCard}>
                  <Text style={styles.statNumber}>{exportStats.totalRecords}</Text>
                  <Text style={styles.statLabel}>Total Records</Text>
                </View>
                <View style={styles.statCard}>
                  <Text style={[styles.statNumber, { color: '#DC2626' }]}>
                    {exportStats.exportedRecords}
                  </Text>
                  <Text style={styles.statLabel}>Exported</Text>
                </View>
                <View style={styles.statCard}>
                  <Text style={[styles.statNumber, { color: theme.colors.primary }]}>
                    {exportStats.fieldsIncluded}
                  </Text>
                  <Text style={styles.statLabel}>Fields</Text>
                </View>
                <View style={styles.statCard}>
                  <Text style={[styles.statNumber, { color: theme.colors.success }]}>
                    {exportStats.fileSize}
                  </Text>
                  <Text style={styles.statLabel}>File Size</Text>
                </View>
              </View>

              {/* Platform Breakdown */}
              <View style={styles.platformSection}>
                <Text style={styles.platformTitle}>Platform Coverage</Text>
                <View style={styles.platformGrid}>
                  <View style={styles.platformCard}>
                    <Ionicons name="car" size={20} color="#000000" />
                    <Text style={styles.platformNumber}>{exportStats.platformBreakdown.uber}</Text>
                    <Text style={styles.platformLabel}>Uber Drivers</Text>
                  </View>
                  <View style={styles.platformCard}>
                    <Ionicons name="car-sport" size={20} color="#00C851" />
                    <Text style={styles.platformNumber}>{exportStats.platformBreakdown.careem}</Text>
                    <Text style={styles.platformLabel}>Careem Drivers</Text>
                  </View>
                  <View style={styles.platformCard}>
                    <Ionicons name="duplicate" size={20} color="#DC2626" />
                    <Text style={styles.platformNumber}>{exportStats.platformBreakdown.both}</Text>
                    <Text style={styles.platformLabel}>Both Platforms</Text>
                  </View>
                  <View style={styles.platformCard}>
                    <Ionicons name="help-circle" size={20} color="#9CA3AF" />
                    <Text style={styles.platformNumber}>{exportStats.platformBreakdown.none}</Text>
                    <Text style={styles.platformLabel}>No Platform</Text>
                  </View>
                </View>
              </View>

              <View style={styles.summaryCard}>
                <View style={styles.summaryRow}>
                  <Text style={styles.summaryLabel}>Export Format:</Text>
                  <Text style={styles.summaryValue}>
                    {exportStats.format}
                  </Text>
                </View>
                <View style={styles.summaryRow}>
                  <Text style={styles.summaryLabel}>Records Exported:</Text>
                  <Text style={[styles.summaryValue, { color: '#DC2626' }]}>
                    {exportStats.exportedRecords}
                  </Text>
                </View>
                <View style={styles.summaryRow}>
                  <Text style={styles.summaryLabel}>Fields Included:</Text>
                  <Text style={styles.summaryValue}>
                    {exportStats.fieldsIncluded} / {exportStats.totalFields}
                  </Text>
                </View>
                <View style={styles.summaryRow}>
                  <Text style={styles.summaryLabel}>Export Date:</Text>
                  <Text style={styles.summaryValue}>
                    {new Date().toLocaleDateString()}
                  </Text>
                </View>
              </View>

              {/* Export-specific insights */}
              <View style={styles.insightCard}>
                <View style={styles.insightHeader}>
                  <Ionicons name="analytics" size={20} color="#DC2626" />
                  <Text style={styles.insightTitle}>Driver Export Analysis</Text>
                </View>
                <Text style={styles.insightText}>
                  • Complete driver database exported with {exportStats.fieldsIncluded} data fields
                </Text>
                <Text style={styles.insightText}>
                  • {exportStats.platformBreakdown.both} drivers work on multiple platforms
                </Text>
                <Text style={styles.insightText}>
                  • Export file size: {exportStats.fileSize} in {exportStats.format} format
                </Text>
                <Text style={styles.insightText}>
                  • Ready for download or integration with external systems
                </Text>
              </View>

              {/* Download Actions */}
              <View style={styles.downloadSection}>
                <Text style={styles.downloadTitle}>Download Options</Text>
                <View style={styles.downloadButtons}>
                  <TouchableOpacity 
                    style={[styles.downloadButton, { backgroundColor: '#DC2626' }]} 
                    onPress={downloadFile}
                  >
                    <Ionicons name="download" size={20} color={theme.colors.white} />
                    <Text style={styles.downloadButtonText}>Download File</Text>
                  </TouchableOpacity>
                  
                  <TouchableOpacity 
                    style={[styles.downloadButton, { backgroundColor: theme.colors.primary }]} 
                    onPress={shareFile}
                  >
                    <Ionicons name="share" size={20} color={theme.colors.white} />
                    <Text style={styles.downloadButtonText}>Share File</Text>
                  </TouchableOpacity>
                </View>
              </View>
            </View>
          )}

          {/* Action Buttons */}
          <View style={styles.actionContainer}>
            {progress.phase === 'idle' || progress.phase === 'complete' ? (
              <>
                <TouchableOpacity 
                  style={[
                    styles.primaryButton, 
                    { backgroundColor: '#DC2626' },
                    exportFields.filter(f => f.enabled).length === 0 && styles.disabledButton
                  ]} 
                  onPress={startExport}
                  disabled={exportFields.filter(f => f.enabled).length === 0 || progress.phase === 'processing'}
                >
                  <Ionicons name="download" size={20} color={theme.colors.white} />
                  <Text style={styles.primaryButtonText}>
                    {progress.phase === 'complete' ? 'Export Again' : 'Start Export'}
                  </Text>
                </TouchableOpacity>
                
                {progress.phase === 'complete' && (
                  <TouchableOpacity 
                    style={[styles.secondaryButton, { borderColor: '#DC2626' }]} 
                    onPress={resetExport}
                  >
                    <Ionicons name="refresh" size={20} color="#DC2626" />
                    <Text style={[styles.secondaryButtonText, { color: '#DC2626' }]}>Reset</Text>
                  </TouchableOpacity>
                )}
              </>
            ) : (
              <TouchableOpacity style={styles.disabledButton} disabled>
                <ActivityIndicator size="small" color={theme.colors.white} />
                <Text style={styles.primaryButtonText}>Processing...</Text>
              </TouchableOpacity>
            )}
          </View>
        </ScrollView>
      </SafeAreaView>
    </AppLayout>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.gray50,
  },
  scrollView: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: theme.spacing.lg,
    backgroundColor: theme.colors.white,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray200,
  },
  iconContainer: {
    width: 48,
    height: 48,
    borderRadius: theme.borderRadius.lg,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: theme.spacing.md,
  },
  headerInfo: {
    flex: 1,
  },
  title: {
    fontSize: theme.typography.sizes.xl,
    fontWeight: '700' as any,
    color: theme.colors.textPrimary,
    marginBottom: 4,
  },
  subtitle: {
    fontSize: theme.typography.sizes.md,
    color: theme.colors.textSecondary,
  },
  section: {
    backgroundColor: theme.colors.white,
    marginTop: theme.spacing.md,
    padding: theme.spacing.lg,
  },
  sectionTitle: {
    fontSize: theme.typography.sizes.lg,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.md,
  },
  sectionSubtitle: {
    fontSize: theme.typography.sizes.md,
    color: theme.colors.textSecondary,
    marginBottom: theme.spacing.lg,
  },
  formatGrid: {
    flexDirection: 'row',
    gap: theme.spacing.md,
  },
  formatCard: {
    flex: 1,
    padding: theme.spacing.md,
    borderWidth: 1,
    borderColor: theme.colors.gray200,
    borderRadius: theme.borderRadius.lg,
    alignItems: 'center',
    position: 'relative',
  },
  formatIcon: {
    width: 40,
    height: 40,
    borderRadius: theme.borderRadius.md,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing.sm,
  },
  formatName: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
    marginBottom: 4,
  },
  formatDescription: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    textAlign: 'center',
  },
  selectedIndicator: {
    position: 'absolute',
    top: theme.spacing.sm,
    right: theme.spacing.sm,
    width: 20,
    height: 20,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  fieldsContainer: {
    gap: theme.spacing.sm,
  },
  fieldItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing.md,
    borderWidth: 1,
    borderColor: theme.colors.gray200,
    borderRadius: theme.borderRadius.lg,
    backgroundColor: theme.colors.gray50,
  },
  fieldItemEnabled: {
    borderColor: '#DC2626',
    backgroundColor: '#DC2626' + '05',
  },
  fieldItemRequired: {
    borderColor: theme.colors.primary,
    backgroundColor: theme.colors.primary + '05',
  },
  fieldInfo: {
    flex: 1,
    marginRight: theme.spacing.md,
  },
  fieldLabel: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '500' as any,
    color: theme.colors.textSecondary,
    marginBottom: 2,
  },
  fieldLabelEnabled: {
    color: '#DC2626',
  },
  fieldLabelRequired: {
    color: theme.colors.primary,
  },
  fieldDescription: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
  },
  requiredMark: {
    color: theme.colors.error,
  },
  fieldSummary: {
    marginTop: theme.spacing.md,
    padding: theme.spacing.sm,
    backgroundColor: theme.colors.gray100,
    borderRadius: theme.borderRadius.md,
    alignItems: 'center',
  },
  fieldSummaryText: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    fontWeight: '500' as any,
  },
  stepsContainer: {
    gap: theme.spacing.md,
  },
  stepItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  stepCircle: {
    width: 32,
    height: 32,
    borderRadius: 16,
    borderWidth: 2,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: theme.spacing.md,
  },
  stepNumber: {
    fontSize: theme.typography.sizes.sm,
    fontWeight: '600' as any,
  },
  stepInfo: {
    flex: 1,
  },
  stepName: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '500' as any,
    marginBottom: 2,
  },
  stepDescription: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
  },
  progressCard: {
    marginTop: theme.spacing.lg,
    padding: theme.spacing.md,
    backgroundColor: theme.colors.gray50,
    borderRadius: theme.borderRadius.lg,
    borderLeftWidth: 4,
  },
  progressHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing.xs,
  },
  progressMessage: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '500' as any,
    color: theme.colors.textPrimary,
  },
  progressDetails: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    marginBottom: theme.spacing.sm,
  },
  progressBar: {
    height: 4,
    backgroundColor: theme.colors.gray200,
    borderRadius: 2,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
  },
  statsContainer: {
    flexDirection: 'row',
    gap: theme.spacing.md,
    marginBottom: theme.spacing.lg,
  },
  statCard: {
    flex: 1,
    alignItems: 'center',
    padding: theme.spacing.md,
    backgroundColor: theme.colors.gray50,
    borderRadius: theme.borderRadius.lg,
  },
  statNumber: {
    fontSize: theme.typography.sizes.xl,
    fontWeight: '700' as any,
    color: theme.colors.textPrimary,
    marginBottom: 4,
  },
  statLabel: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    textAlign: 'center',
  },
  platformSection: {
    marginBottom: theme.spacing.lg,
  },
  platformTitle: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.md,
  },
  platformGrid: {
    flexDirection: 'row',
    gap: theme.spacing.sm,
  },
  platformCard: {
    flex: 1,
    alignItems: 'center',
    padding: theme.spacing.sm,
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.md,
    borderWidth: 1,
    borderColor: theme.colors.gray200,
  },
  platformNumber: {
    fontSize: theme.typography.sizes.lg,
    fontWeight: '700' as any,
    color: theme.colors.textPrimary,
    marginTop: theme.spacing.xs,
    marginBottom: 2,
  },
  platformLabel: {
    fontSize: theme.typography.sizes.xs,
    color: theme.colors.textSecondary,
    textAlign: 'center',
  },
  summaryCard: {
    padding: theme.spacing.lg,
    backgroundColor: theme.colors.gray50,
    borderRadius: theme.borderRadius.lg,
    gap: theme.spacing.sm,
    marginBottom: theme.spacing.md,
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  summaryLabel: {
    fontSize: theme.typography.sizes.md,
    color: theme.colors.textSecondary,
  },
  summaryValue: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
  },
  insightCard: {
    padding: theme.spacing.lg,
    backgroundColor: '#DC2626' + '10',
    borderRadius: theme.borderRadius.lg,
    borderWidth: 1,
    borderColor: '#DC2626' + '20',
    marginBottom: theme.spacing.lg,
  },
  insightHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: theme.spacing.sm,
    gap: theme.spacing.xs,
  },
  insightTitle: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as any,
    color: '#DC2626',
  },
  insightText: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textSecondary,
    marginBottom: theme.spacing.xs,
    lineHeight: 18,
  },
  downloadSection: {
    marginTop: theme.spacing.lg,
    paddingTop: theme.spacing.lg,
    borderTopWidth: 1,
    borderTopColor: theme.colors.gray200,
  },
  downloadTitle: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.md,
  },
  downloadButtons: {
    flexDirection: 'row',
    gap: theme.spacing.md,
  },
  downloadButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.lg,
    borderRadius: theme.borderRadius.lg,
    gap: theme.spacing.sm,
  },
  downloadButtonText: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as any,
    color: theme.colors.white,
  },
  actionContainer: {
    padding: theme.spacing.lg,
    gap: theme.spacing.md,
  },
  primaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.lg,
    borderRadius: theme.borderRadius.lg,
    gap: theme.spacing.sm,
  },
  primaryButtonText: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as any,
    color: theme.colors.white,
  },
  secondaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.white,
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.lg,
    borderRadius: theme.borderRadius.lg,
    borderWidth: 1,
    gap: theme.spacing.sm,
  },
  secondaryButtonText: {
    fontSize: theme.typography.sizes.md,
    fontWeight: '600' as any,
  },
  disabledButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.colors.gray400,
    paddingVertical: theme.spacing.md,
    paddingHorizontal: theme.spacing.lg,
    borderRadius: theme.borderRadius.lg,
    gap: theme.spacing.sm,
  },
});
